import { StoresRepository } from '@lib/api/repository/stores/stores.repository';
import AppError from '@lib/app-error';
import { AdminStoresFilters, StoreInput, StoreReview } from '@lib/types';

import { Requester } from '../clients';

import {
  AdminStoreDTO,
  AdminStoresPageDTO,
  OwnerStoreDTO,
  PublicStoreDTO,
  PublicStoreProgressDTO,
  createAdminStore,
  createAdminStoresPage,
  createOwnerStore,
  createPublicStore,
  createPublicStoreProgress,
  createPublicStores,
} from './models';

const STORE_NOT_FOUND_ERROR_CODE = 'store-not-found';

const isStoreNotFoundError = (error: unknown) =>
  error instanceof AppError &&
  error.isNotFound &&
  error.code === STORE_NOT_FOUND_ERROR_CODE;

export default class StoresAPI implements StoresRepository {
  async checkSlug(slug: string, signal?: AbortSignal) {
    return (
      await Requester.get<{
        slug: string;
        available: boolean;
        reason?: 'invalid' | 'reserved' | 'taken' | 'blocked';
      }>(`/marketplace/stores/slug-availability/${encodeURIComponent(slug)}`, {
        signal,
      })
    ).data;
  }

  async list(signal?: AbortSignal) {
    const response = await Requester.get<PublicStoreDTO[]>(
      '/marketplace/stores',
      { signal },
    );

    return createPublicStores(response.data);
  }

  async find(slug: string, signal?: AbortSignal) {
    try {
      const response = await Requester.get<PublicStoreDTO>(
        `/marketplace/stores/${slug}`,
        { signal },
      );

      return createPublicStore(response.data);
    } catch (error) {
      if (isStoreNotFoundError(error)) {
        return null;
      }

      throw error;
    }
  }

  async findMine(signal?: AbortSignal) {
    try {
      const response = await Requester.get<OwnerStoreDTO>(
        '/marketplace/stores/me',
        { signal },
      );

      return createOwnerStore(response.data);
    } catch (error) {
      if (isStoreNotFoundError(error)) {
        return null;
      }

      throw error;
    }
  }

  async save(input: StoreInput) {
    const response = await Requester.post<OwnerStoreDTO>(
      '/marketplace/stores',
      {
        body: input,
      },
    );

    return createOwnerStore(response.data);
  }

  async listAdmin(filters: AdminStoresFilters, signal?: AbortSignal) {
    const response = (
      await Requester.get<AdminStoresPageDTO>('/marketplace/admin/stores', {
        signal,
        params: { ...filters },
      })
    ).data;

    return createAdminStoresPage(response);
  }

  async setActive(id: string, active: boolean) {
    const response = (
      await Requester.patch<AdminStoreDTO>(
        `/marketplace/admin/stores/${id}/${active}`,
      )
    ).data;

    return createAdminStore(response);
  }

  async setMineActive(active: boolean) {
    const response = (
      await Requester.patch<OwnerStoreDTO>(
        `/marketplace/stores/me/active/${active}`,
      )
    ).data;

    return createOwnerStore(response);
  }

  async listReviews(slug: string, signal?: AbortSignal) {
    return (
      await Requester.get<StoreReview[]>(
        `/marketplace/stores/${slug}/reviews`,
        { signal },
      )
    ).data;
  }

  async findProgress(slug: string, signal?: AbortSignal) {
    const response = (
      await Requester.get<PublicStoreProgressDTO>(
        `/marketplace/store-progress/public/${slug}`,
        { signal },
      )
    ).data;

    return createPublicStoreProgress(response);
  }

  async acknowledgePromotion() {
    await Requester.post(
      '/marketplace/store-progress/me/acknowledge-promotion',
    );
  }
}
